'use client';

import { isFunctionValue } from '../lib/validation/primitives';


import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface ProductMockupVideoProps {
  webmSrc: string;
  mp4Src: string;
}

export function ProductMockupVideo({ webmSrc, mp4Src }: ProductMockupVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mounted, setMounted] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 768px)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const reducedData = window.matchMedia('(prefers-reduced-data: reduce)');

    const syncShouldLoad = () => {
      const isDesktop = desktop.matches;
      const skipForMotion = reducedMotion.matches;
      const skipForData = reducedData.matches;
      setShouldLoadVideo(isDesktop && !skipForMotion && !skipForData);
    };

    syncShouldLoad();
    desktop.addEventListener('change', syncShouldLoad);
    reducedMotion.addEventListener('change', syncShouldLoad);
    reducedData.addEventListener('change', syncShouldLoad);

    return () => {
      desktop.removeEventListener('change', syncShouldLoad);
      reducedMotion.removeEventListener('change', syncShouldLoad);
      reducedData.removeEventListener('change', syncShouldLoad);
    };
  }, []);

  useEffect(() => {
    if (!shouldLoadVideo) return;

    let cancelled = false;

    const mountVideo = () => {
      if (cancelled) return;
      setMounted(true);
    };

    const queueMount = () => {
      if (isFunctionValue(window.requestIdleCallback)) {
        const requestIdleCallback = window.requestIdleCallback.bind(window);
        const cancelIdleCallback = window.cancelIdleCallback.bind(window);
        const idleId = requestIdleCallback(() => mountVideo(), { timeout: 2000 });

        return () => cancelIdleCallback(idleId);
      }

      const timeoutId = window.setTimeout(mountVideo, 1200);

      return () => window.clearTimeout(timeoutId);
    };

    let cleanupIdle = () => {};

    if (document.readyState === 'complete') {
      cleanupIdle = queueMount();
    } else {
      const handleLoad = () => {
        cleanupIdle = queueMount();
      };

      window.addEventListener('load', handleLoad, { once: true });

      return () => {
        cancelled = true;
        cleanupIdle();
        window.removeEventListener('load', handleLoad);
      };
    }

    return () => {
      cancelled = true;
      cleanupIdle();
    };
  }, [shouldLoadVideo]);

  useEffect(() => {
    const video = videoRef.current;

    if (!mounted || !video) return;

    const reveal = () => setVideoReady(true);

    video.load();
    video.play().then(reveal).catch(() => {
      reveal();
    });
  }, [mounted]);

  if (!shouldLoadVideo || !mounted) return null;

  return (
    <video
      ref={videoRef}
      className={cn(
        'absolute inset-0 h-full w-full object-cover transition-opacity duration-500',
        videoReady ? 'opacity-100' : 'opacity-0'
      )}
      width={1920}
      height={1080}
      muted
      playsInline
      loop
      preload="none"
      aria-hidden="true"
    >
      <source src={webmSrc} type="video/webm" />
      <source src={mp4Src} type="video/mp4" />
    </video>
  );
}
